// One database over time (`/admin/system/databases/:dbId`): NextAdmin demo `/analytics` with `EntityTabs`-style tabs
// (Activity · Queries · Storage · Maintenance), a period control, and chart and table cards.
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Database } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { DashboardGrid } from '../../../components/page/DashboardPage.js';
import { DetailPage } from '../../../components/page/DetailPage.js';
import { Badge, Callout, EmptyState } from '../../../components/ui/index.js';
import { getSystemDatabaseOptions } from '../../../generated/api/@tanstack/react-query.gen.js';
import { PAGE_HELP } from '../../../help/index.js';
import { useTabParam } from '../../../hooks/useTabParam.js';
import { ApiError } from '../../../services/api/errors.js';
import { databaseName } from './DatabaseCard.js';
import { DatabaseMetricChart, type DatabasePanel } from './DatabaseMetricChart.js';
import { JobsCard } from './JobsCard.js';
import { StatementsCard } from './StatementsCard.js';
import { StorageCards } from './StorageCards.js';
import { formatBytes, formatCount, formatSeconds, type SystemDatabaseId } from './metric-format.js';
import { RangePills, useRangeParam } from './RangePills.js';

const TAB_IDS = ['activity', 'queries', 'storage', 'maintenance'] as const;
const TABS = [
  { id: 'activity', label: 'Activity' },
  { id: 'queries', label: 'Queries' },
  { id: 'storage', label: 'Storage' },
  { id: 'maintenance', label: 'Maintenance' },
];

const ACTIVITY: ReadonlyArray<{ panel: DatabasePanel; title: string; description: string; area?: boolean }> = [
  { panel: 'transactions', title: 'Transactions', description: 'Committed and rolled back, per second' },
  { panel: 'rows', title: 'Rows', description: 'Read and written per second' },
  { panel: 'connections', title: 'Connections', description: 'By state: active, idle, idle in a transaction' },
  { panel: 'connections-used', title: 'Connections used', description: 'Share of max_connections' },
  { panel: 'cache-hit', title: 'Read from memory', description: 'Share of reads served from shared buffers' },
  { panel: 'locks', title: 'Locks', description: 'Sessions waiting on a lock, and idle inside a transaction' },
  {
    panel: 'transaction-age',
    title: 'Longest transaction and query',
    description: 'Long ones hold locks and block vacuum',
  },
  { panel: 'temp', title: 'Temp files', description: 'Bytes spilled to disk by sorts and hashes, per second' },
  { panel: 'wal', title: 'WAL written', description: 'Write-ahead log per second: the write load' },
];

const DATABASE_IDS: readonly SystemDatabaseId[] = ['app', 'market'];

export function SystemDatabasePage() {
  const { dbId = '' } = useParams<{ dbId: string }>();
  const databaseId = DATABASE_IDS.find((id) => id === dbId);
  const [tab, setTab] = useTabParam(TAB_IDS, 'activity');
  const [range, setRange] = useRangeParam();
  const query = useQuery({
    ...getSystemDatabaseOptions({ path: { id: databaseId ?? 'app' } }),
    enabled: databaseId !== undefined,
    refetchInterval: 30_000,
  });
  const database = query.data;
  const title = databaseId ? databaseName(databaseId) : 'Database';

  if (!databaseId) {
    return (
      <DetailPage title="Database" breadcrumbs={[{ label: 'System', to: '/admin/system' }, { label: 'Database' }]}>
        <EmptyState icon={Database} title="No such database" description={`"${dbId}" is not a monitored database.`} />
      </DetailPage>
    );
  }

  return (
    <DetailPage
      title={title}
      subtitle="How this database works now and over time"
      breadcrumbs={[{ label: 'System', to: '/admin/system' }, { label: title }]}
      helpContent={PAGE_HELP['system-database']}
      badges={database?.hostId && <Badge variant="neutral">on {database.hostId}</Badge>}
      actions={
        tab === 'activity' || tab === 'queries' || tab === 'maintenance' ? (
          <RangePills range={range} onChange={setRange} />
        ) : undefined
      }
      meta={
        database && (
          <span>
            {formatBytes(database.sizeBytes)} · {formatCount(database.connections)} of{' '}
            {formatCount(database.maxConnections)} connections · longest transaction{' '}
            {formatSeconds(database.longestTransactionSeconds)}
          </span>
        )
      }
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
      alert={
        query.isError ? (
          <Callout tone="danger" icon={AlertTriangle} title="This database could not be loaded">
            {query.error instanceof ApiError ? query.error.message : 'The request failed.'}
          </Callout>
        ) : undefined
      }
    >
      <DashboardGrid>
        {tab === 'activity' &&
          ACTIVITY.map((chart) => (
            <div key={chart.panel} className="xl:col-span-6">
              <DatabaseMetricChart
                databaseId={databaseId}
                panel={chart.panel}
                range={range}
                title={chart.title}
                description={chart.description}
                area={chart.area}
              />
            </div>
          ))}
        {tab === 'queries' && (
          <StatementsCard
            databaseId={databaseId}
            range={range}
            collected={database?.statementsCollected ?? false}
            table={database?.statementTable ?? null}
          />
        )}
        {tab === 'storage' && (
          <>
            <div className="xl:col-span-12">
              <DatabaseMetricChart
                databaseId={databaseId}
                panel="size"
                range="90d"
                title="Size over 90 days"
                description="The whole database, measured hourly"
                area
              />
            </div>
            {database && <StorageCards database={database} />}
          </>
        )}
        {tab === 'maintenance' && (
          <>
            <div className="xl:col-span-12">
              <DatabaseMetricChart
                databaseId={databaseId}
                panel="xid-age"
                range={range}
                title="Transaction ID age"
                description="Vacuum must freeze rows before this reaches about 2 billion"
              />
            </div>
            {database && <JobsCard jobs={database.jobs} />}
          </>
        )}
      </DashboardGrid>
    </DetailPage>
  );
}
